import { ActionIcon } from '@cowbell/ui/core';
import { faCircleExclamation, faFile } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { UploadEntry } from '../../uploads/use-uploads';
import { CloseIcon } from '../ShellDrawer/icons';
import token from '../Composer/Token.module.css';
import styles from './UploadChipStrip.module.css';

export interface UploadChipStripProps {
  readonly uploads: UploadEntry[];
  readonly onRemove: (id: string) => void;
}

function statusLabel(entry: UploadEntry): string | null {
  if (entry.status === 'uploading') return 'Uploading…';
  if (entry.status === 'error') return entry.error ?? 'Upload failed';
  return null;
}

/** Uploaded files as tokens in the composer's token row (the wrapper is
 *  display: contents, so they flow alongside context tokens). A ring spins
 *  while a file goes up, an error turns the token red with the reason, and
 *  remove aborts a running upload. Completed uploads stay until the user
 *  sends or removes them — they become DocumentContext entries on the next
 *  send. */
export function UploadChipStrip({ uploads, onRemove }: UploadChipStripProps) {
  if (uploads.length === 0) return null;
  return (
    <div className={styles.strip} data-testid="upload-chip-strip">
      {uploads.map((entry) => {
        const label = statusLabel(entry);
        return (
          <span
            key={entry.id}
            className={token.token}
            data-status={entry.status}
            title={label ? `${entry.name} — ${label}` : entry.name}
          >
            <UploadMark status={entry.status} />
            <span className={token.tokenName}>{entry.name}</span>
            {label && <span className={token.tokenMeta}>{label}</span>}
            <span className={token.tokenActions}>
              <ActionIcon
                className={token.tokenAction}
                variant="transparent"
                aria-label={`Remove ${entry.name}`}
                onClick={() => onRemove(entry.id)}
              >
                <CloseIcon size={12} />
              </ActionIcon>
            </span>
          </span>
        );
      })}
    </div>
  );
}

function UploadMark({ status }: Readonly<{ status: UploadEntry['status'] }>) {
  if (status === 'uploading') {
    return <span className={token.progress} aria-hidden />;
  }
  return (
    <FontAwesomeIcon
      icon={status === 'error' ? faCircleExclamation : faFile}
      className={token.tokenIcon}
    />
  );
}
